/**
 * useAuthRouting — URL-based routing for unauthenticated auth screens.
 *
 * Manages the /login and /register routes while the user is not authenticated.
 * This is intentionally kept separate from useUrlSync, which only runs inside
 * the authenticated AppContent tree.
 *
 * Pathname → auth page mapping (Rule 11: explicit, no silent fallback):
 *   /register → 'register'
 *   /login    → 'login'
 *   /         → 'login'  (explicit product default)
 *   anything else → 'login' (explicit intentional default — show login, not an error)
 */

import { useState, useEffect, useCallback } from 'react';

export type AuthPage = 'login' | 'register';

/**
 * Derive which auth page to show from a given pathname.
 *
 * The default is "login" — this is an explicit, intentional product decision
 * (Rule 11: no silent fallback). Unknown unauthenticated paths fall back to
 * the login screen as the safest option.
 */
export function authPageFromPathname(pathname: string): AuthPage {
  if (pathname === '/register') return 'register';
  if (pathname === '/login') return 'login';
  // Explicit default — Rule 11 compliant: this is intentional, not swallowed.
  return 'login';
}

/** Legal pages reachable regardless of authentication state (SHA-24). */
export type LegalPage = 'terms' | 'privacy';

/**
 * Derive which legal page (if any) a pathname refers to.
 *
 * Unlike authPageFromPathname, this has NO fallback — most paths are not
 * legal pages, so `null` is the correct (non-swallowed) result for anything
 * that isn't exactly /terms or /privacy.
 */
export function legalPageFromPathname(pathname: string): LegalPage | null {
  if (pathname === '/terms') return 'terms';
  if (pathname === '/privacy') return 'privacy';
  return null;
}

interface UseAuthRoutingResult {
  /** Which auth page is currently active. */
  authPage: AuthPage;
  /** Navigate to /register and update state. */
  navigateToRegister: () => void;
  /** Navigate to /login and update state. */
  navigateToLogin: () => void;
  /**
   * Call after successful login/register.
   * Replaces the current URL with / so the address bar doesn't stay on /login or /register
   * when the authenticated app mounts.
   */
  redirectToApp: () => void;
}

/**
 * Hook that synchronises the auth screen (login/register) with the URL.
 *
 * - Derives initial page from window.location.pathname.
 * - navigateToRegister / navigateToLogin: pushState + update state.
 * - Listens to popstate so back/forward works between /login and /register.
 * - redirectToApp: replaces URL with / after successful authentication.
 */
export function useAuthRouting(): UseAuthRoutingResult {
  const [authPage, setAuthPage] = useState<AuthPage>(() =>
    authPageFromPathname(window.location.pathname),
  );

  // Handle browser back/forward between /login and /register.
  useEffect(() => {
    function handlePopState(): void {
      setAuthPage(authPageFromPathname(window.location.pathname));
    }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const navigateToRegister = useCallback((): void => {
    window.history.pushState({}, '', '/register');
    setAuthPage('register');
  }, []);

  const navigateToLogin = useCallback((): void => {
    window.history.pushState({}, '', '/login');
    setAuthPage('login');
  }, []);

  const redirectToApp = useCallback((): void => {
    // Replace so the user can't back-navigate into /login or /register.
    window.history.replaceState({}, '', '/');
  }, []);

  return { authPage, navigateToRegister, navigateToLogin, redirectToApp };
}
